import React, { useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import fp from 'lodash/fp';
import moment from 'moment';

import Grid from '../../webcommon/grid/Grid';
import FilterHeader from '../../webcommon/header/filterheader/FilterHeader';
import { getTimeStamp } from '../../webcommon/common/helpers';
import { getFilters } from '../../webcommon/header/filterheader/selectors';

import {
    initialise,
    searchId,
    refreshData,
    setGridSorts
} from './actions';
import * as styles from './styles.module.scss';
import * as selectors from './selectors';
import * as appConstants from '../../common/constants';
import GridHeader from '../../common/gridheader/GridHeader';
import gridStyles from './GridOverride.module.scss';
import ExportAsCsvButton from '../../common/gridheader/exportbutton/ExportAsCsvButton';
import { findGridSort } from '../../common/helpermethods/HelperMethods';


const ImpactAnalysisIdSummary = (props) => {
    const { initialise, setGridSorts } = props;
    useEffect(() => {
        initialise();
    }, [initialise]);

    const calculateCsvFilename = useMemo(() => {
        const title = 'ImpactAnalysisId';
        const startDate = moment(props.dateFrom).format(appConstants.dateFormat);
        const endDate = moment(props.dateTo).format(appConstants.dateFormat);
        return props.user.length
            ? fp.compact([title, startDate, endDate, props.user]).join('_').concat('.csv')
            : fp.compact([title, startDate, endDate]).join('_').concat('.csv');
    }, [props.dateFrom, props.dateTo, props.user]);

    const gridSortings = useCallback((newSorted) => {
        setGridSorts('ImpactAnalysisId', newSorted);
    }, [setGridSorts]);

    const processDataForCsvExport = (rows) => {
        const mapDataKeys = row => ({ Date: row.date, 'User Name': row.userName, 'Impact Analysis ID': row.id });
        return rows.map(mapDataKeys);
    };

    return (
        <div className={ styles.idGrid }>
            <div className={ styles.headerWrapper }>
                <FilterHeader
                    fixed
                    title="Impact Analysis ID Summary"
                    id="SummaryIdGrid"
                />
            </div>
            <GridHeader
                lastUpdated={ props.lastUpdated }
                refreshControlsActive
                refreshData={ props.refreshData }
            >
                <ExportAsCsvButton
                    csvData={ props.rows }
                    csvFilename={ calculateCsvFilename }
                    csvMapping={ processDataForCsvExport }
                />
            </GridHeader>
            <div className={ styles.gridContainer }>
                {props.columns.length ? (
                    <Grid
                        columns={ props.columns }
                        defaultColumnSpacing={ 2 }
                        rows={ props.rows }
                        onRowClick={ row => props.searchId(row.id) }
                        styles={ gridStyles }
                        loading={ props.loading }
                        defaultSorted={ findGridSort(props.defaultSort, 'ImpactAnalysisId') }
                        onSortedChange={ gridSortings }
                    />
                ) : null}
            </div>
        </div>
    );
};

ImpactAnalysisIdSummary.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string,
            type: PropTypes.string
        })
    ),
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    initialise: PropTypes.func.isRequired,
    searchId: PropTypes.func.isRequired,
    lastUpdated: PropTypes.number,
    loading: PropTypes.bool,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    user: PropTypes.string,
    refreshData: PropTypes.func.isRequired,
    defaultSort: PropTypes.arrayOf(
        PropTypes.shape({
            gridId: PropTypes.string,
            gridSort: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.string,
                desc: PropTypes.bool
            }))
        })
    ),
    setGridSorts: PropTypes.func.isRequired
};

ImpactAnalysisIdSummary.defaultProps = {
    columns: [],
    rows: [],
    lastUpdated: getTimeStamp(),
    loading: false,
    dateFrom: '',
    dateTo: '',
    user: '',
    defaultSort: []
};

const mapStateToProps = state => ({
    columns: selectors.combinedColumnsSelector(state),
    rows: selectors.getRows(state),
    lastUpdated: selectors.getLastUpdated(state),
    loading: selectors.getLoading(state),
    defaultSort: selectors.getDefaultSort(state),
    dateFrom: selectors.getDateFrom(getFilters(state, 'SummaryIdGrid')),
    dateTo: selectors.getDateTo(getFilters(state, 'SummaryIdGrid')),
    user: selectors.getUser(getFilters(state, 'SummaryIdGrid'))
});

const mapDispatchToProps = {
    initialise,
    searchId,
    refreshData,
    setGridSorts
};

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(ImpactAnalysisIdSummary));

export { ImpactAnalysisIdSummary as ImpactAnalysisIdSummaryUnconnected };
